import React from 'react';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import EnhancedTable from '../../../components/table';
import { TooltipButton } from '../../../components/tooltip-button';
import MainLoading from '../../../components/main-loading';
import { AnchorBlock } from '../../../components/anchorBlock';
// Modules
import { actions as adminActions, selectors as adminSelectors } from '../../../redux/reducers/admin';
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { IDRoleDetail, RoleDetailById } from '../../../redux/reducers/api-types';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import UserUtils from '../../../utils/userUtils';
import ObjectUtils from '../../../utils/objectUtils';
import { paths } from '../../paths';
import { generateOptions } from '../../../utils/businessUtils';
import StringUtils from '../../../utils/stringUtils';
import { FormatType, HeadCell, PickerOption } from '../../../local-types';
import { Cell, createCell } from '../../../components/table/table-cell-creator';

const queryString = require('query-string');

interface AdminProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    roles: RoleDetailById;
    rights: string[];
    getAllRoles: () => any;
}

class AdminListPage extends React.Component<AdminProps> {
    tableRef: any;

    filterParams: any;

    constructor(props: AdminProps) {
        super(props);
        this.filterParams = queryString.parse(this.props.location.search);

        if (ObjectUtils.isEmpty(props.roles)) {
            props.getAllRoles();
        }
    }

    generateRolesOptions = (): PickerOption[] => {
        const { roles } = this.props;
        const values = Object.values(roles);
        return generateOptions(values, 'uuid', 'tag');
    };

    headerBuilder = (): HeadCell[] => {
        const { t, globalClasses } = this.props;
        const headers: HeadCell[] = [
            createCell(t, Cell.ID),
            createCell(t, Cell.FULLNAME, { id: 'name', contains: false, sortable: false }),
            createCell(t, Cell.EMAIL, { contains: false, sortable: false }),
            {
                id: 'roles',
                label: t('matrix.roles'),
                type: FormatType.PickerMultiple,
                sortable: false,
                pickerOptions: this.generateRolesOptions(),
                formatCell: (row) => row.roles.map((r) => r.tag).join(', '),
            },
            {
                id: 'actions',
                label: t('common:action'),
                filterable: false,
                sortable: false,
                formatCell: (row) => {
                    return (
                        <>
                            <TooltipButton title={t('common:edit')}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    style={{ marginRight: 8 }}
                                    component={RouterLink}
                                    to={`${paths.admin}/${row.id}`}
                                    className={globalClasses.btnAutoWidth}
                                >
                                    <Icon fontSize="small">edit</Icon>
                                </Button>
                            </TooltipButton>
                        </>
                    );
                },
            },
        ];
        return headers;
    };

    renderRoleRows = (roles: IDRoleDetail[]) => {
        const { rights } = this.props;
        return rights.map((row) => {
            return (
                <TableRow key={row}>
                    <TableCell scope="row">{row}</TableCell>
                    {roles.map((role) => (
                        <TableCell key={`${role.tag}/${row}`} style={{ textAlign: 'center' }}>
                            {role.rights.find((r) => r.tag === row)?.grant ?? ''}
                        </TableCell>
                    ))}
                </TableRow>
            );
        });
    };

    renderRoleMatrix = () => {
        const { t, roles } = this.props;
        if (ObjectUtils.isEmpty(roles)) {
            return <MainLoading center />;
        }
        const values = Object.values(roles);
        return (
            <AnchorBlock
                id="matrix"
                title={t('')}
                items={
                    <Table size="small" aria-label="Table roles">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ verticalAlign: 'bottom' }}>{t('matrix.rights')}</TableCell>
                                {values.map((role) => (
                                    <TableCell key={role.tag} style={{ writingMode: 'vertical-rl', textAlign: 'end' }}>
                                        {role.tag}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>{this.renderRoleRows(values)}</TableBody>
                    </Table>
                }
            />
        );
    };

    render() {
        const { t } = this.props;

        return (
            <>
                <App
                    hasRole={UserUtils.hasRoleAdminIDOrGod()}
                    disableBreadcrumb
                    header={{
                        title: t('title'),
                    }}
                >
                    <EnhancedTable
                        onRef={(ref) => (this.tableRef = ref)}
                        headers={this.headerBuilder()}
                        type={tableRequestPoints.ID_EMPLOYEES}
                        filterParams={this.filterParams}
                        noPage={true}
                    />
                    {this.renderRoleMatrix()}
                </App>
            </>
        );
    }
}

const AdminListPageWithStyleAndTransaltion = withGlobalStyles(withTranslation(['admin', 'common'])(AdminListPage));

const mapStateToProps = (state) => ({
    roles: adminSelectors.getRoles(state),
    rights: adminSelectors.getRights(state),
});

const mapDispatchToProps = {
    getAllRoles: adminActions.getAllRoles,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminListPageWithStyleAndTransaltion);
